import React from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import { ArrowCircleRight, Lightbulb, LockOpen, LockPerson, Outbound, Pending, QuestionMark, ThumbUp } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import { generateClassName, getIsoDate } from '../../b2b-common/helpers';

// COMMON

export const getIconWithTooltip = (IconComponent, iconStyle, tooltip, className = null, onClick = null) => {
    return (
        <Tooltip title={tooltip} className={generateClassName('b2b-icon-with-tooltip', '', className)}>
            <IconComponent 
                style={{ ...iconStyle }} 
                className={generateClassName('b2b-icon', '', className)} 
                onClick={onClick} 
            />
        </Tooltip>
    );
}

export const TrendIcon = ({ trend, withTooltip = true, locale = null }) => {
    const { t } = useB2BTranslation('b2b-plans', locale);
    const iconStyle = {
        borderRadius: '50%',
        padding: '1px',
        color: '#3233C1',
        backgroundColor: 'white',
        fontSize: '28px',
    };
    switch (trend) {
        case 'IMPROVING':
            if (withTooltip) {
                return getIconWithTooltip(Outbound, { ...iconStyle }, t('Improving'), 'trend-icon');
            } else {
                return <Outbound style={{ ...iconStyle }} className='trend-icon' />;
            }
        case 'NOT_IMPROVING':
            if (withTooltip) {
                return getIconWithTooltip(Outbound, { ...iconStyle, transform: 'scaleY(-1)' }, t('Detereorating'), 'trend-icon');
            } else {
                return <Outbound style={{ ...iconStyle, transform: 'scaleY(-1)' }} className='trend-icon' />;
            }
        case 'FLAT':
            if (withTooltip) {
                return getIconWithTooltip(ArrowCircleRight, { ...iconStyle }, t('Flat'), 'trend-icon');
            } else {
                return <ArrowCircleRight style={{ ...iconStyle }} className='trend-icon' />;
            }
        default:
            if (withTooltip) {
                return getIconWithTooltip(QuestionMark, { ...iconStyle }, t('No Data'), 'trend-icon');
            } else {
                return <QuestionMark style={{ ...iconStyle }} className='trend-icon' />;
            }
    }
}

export const StatusIcon = ({ status, withTooltip = true, locale = null }) => {
    const { t } = useB2BTranslation('b2b-plans', locale);
    const iconStyle = {
        borderRadius: '50%',
        padding: '1px',
        backgroundColor: 'white',
        fontSize: '28px',
    };
    switch (status) {
        case 'GREEN':
            if (withTooltip) {
                return getIconWithTooltip(CircleIcon, { ...iconStyle, color: '#12BB6A' }, t('On Track'), 'status-icon');
            } else {
                return <CircleIcon style={{ ...iconStyle, color: '#12BB6A' }} className='status-icon' />;
            }
        case 'YELLOW':
            if (withTooltip) {
                return getIconWithTooltip(CircleIcon, { ...iconStyle, color: '#EFC222' }, t('At Risk'), 'status-icon');
            } else {
                return <CircleIcon style={{ ...iconStyle, color: '#EFC222' }} className='status-icon' />;
            }
        case 'RED':
            if (withTooltip) {
                return getIconWithTooltip(CircleIcon, { ...iconStyle, color: '#FF0000' }, t('Off Track'), 'status-icon');
            } else {
                return <CircleIcon style={{ ...iconStyle, color: '#FF0000' }} className='status-icon' />;
            }
        default: // 'DARK GRAY'
            if (withTooltip) {
                return getIconWithTooltip(CircleIcon, { ...iconStyle, color: 'rgb(193, 193, 193)' }, t('No Data'), 'status-icon');
            } else {
                return <CircleIcon style={{ ...iconStyle, color: 'rgb(193, 193, 193)' }} className='status-icon' />;
            }
    }
};

export const StatusTrendIcon = ({ status, trend,
    showStatus = true, showTrend = true,
    withTooltip = true, locale = null }) => {
    
    const { t } = useB2BTranslation('b2b-plans', locale);

    const color =
        status === 'GREEN' ? '#12BB6A' :
        status === 'YELLOW' ? '#EFC222' :
        status === 'RED' ? '#FF0000' : 'rgb(193, 193, 193)';
    const statusText =
        status === 'GREEN' ? t('On Track') :
        status === 'YELLOW' ? t('At Risk') :
        status === 'RED' ? t('Off Track') : t('No Data');
    const trendText =
        trend === 'IMPROVING' ? t('Improving') :
        trend === 'NOT_IMPROVING' ? t('Detereorating') :
        trend === 'FLAT' ? t('Flat') : t('No Data');

    const tooltip = `${statusText} | ${trendText}`;

    const iconStyle = {
        borderRadius: '50%',
        padding: '1px',
        color: color,
        backgroundColor: 'white',
        fontSize: '28px',
    };

    switch (trend) {
        case 'IMPROVING':
            if (withTooltip) {
                return getIconWithTooltip(Outbound, { ...iconStyle }, tooltip);
            } else {
                return <Outbound style={{ ...iconStyle }} />;
            }
        case 'NOT_IMPROVING':
            if (withTooltip) {
                return getIconWithTooltip(Outbound, { ...iconStyle, transform: 'scaleY(-1)' }, tooltip);
            } else {
                return <Outbound style={{ ...iconStyle, transform: 'scaleY(-1)' }} />;
            }
        case 'FLAT':
            if (withTooltip) {
                return getIconWithTooltip(ArrowCircleRight, { ...iconStyle }, tooltip);
            } else {
                return <ArrowCircleRight style={{ ...iconStyle }} />;
            }
        default:
            if (withTooltip) {
                return getIconWithTooltip(QuestionMark, { ...iconStyle }, tooltip);
            } else {
                return <QuestionMark style={{ ...iconStyle }} />;
            }
    }
}

/**
 * Calculates the time progress based on the start and end dates.
 * 
 * @param {string} startDate - The start date of the time period.
 * @param {string} endDate - The end date of the time period.
 * @returns {number} - The time progress as a percentage.
 */

export const calculateTimeProgress = (startDate, endDate, referenceDate = null) => {
    const ref = !referenceDate ? new Date() : referenceDate;
    const start = getIsoDate(startDate);
    const end = getIsoDate(endDate);

    if (ref < start) {
        return 0;
    }

    if (ref > end) {
        return 100;
    }

    const total = end - start;
    const elapsed = ref - start;
    return Math.round((elapsed / total) * 100);
}

/**
 * Finds the available date range between a start date and an end date, considering disabled date ranges.
 *
 * @param {string} startDate - The start date of the range.
 * @param {string} endDate - The end date of the range.
 * @param {Array} disabledDateRanges - An array of disabled date ranges.
 * @returns {Object|null} - An object containing the available start date and end date in the format 'YYYY-MM-DD', or null if no available dates are found.
 */

export const findAvailableDateRange = (startDate, endDate, disabledDateRanges) => {
    // console.log('startDate:', startDate);
    // console.log('endDate:', endDate);
    // console.log('disabledDateRanges:', disabledDateRanges);
    let availableStartDate = null;
    let availableEndDate = null;
    for (let date = dayjs(startDate); date.isBefore(dayjs(endDate)) || date.isSame(dayjs(endDate)); date = date.add(1, 'day')) {
        const isDisabled = disabledDateRanges.some(range =>
            date.isSame(dayjs(range.start).startOf('day')) ||
            date.isSame(dayjs(range.end).endOf('day')) ||
            (date.isAfter(dayjs(range.start).startOf('day')) && date.isBefore(dayjs(range.end).endOf('day')))
        );

        if (!isDisabled) {
            if (!availableStartDate) {
                availableStartDate = date;
            }
            availableEndDate = date;
        } else if (availableStartDate) {
            break;
        }
    }
    if (!availableStartDate || !availableEndDate) {
        return null; // No available dates found
    }
    // console.log('availableStartDate:', availableStartDate.format('YYYY-MM-DD'));
    // console.log('availableEndDate:', availableEndDate.format('YYYY-MM-DD'));
    return {
        availableStartDate: availableStartDate.format('YYYY-MM-DD'),
        availableEndDate: availableEndDate.format('YYYY-MM-DD')
    };
};

export const getValidDate = (disabledDateRanges) => {
    const today = dayjs();

    // Parse and sort the disabled date ranges by start date
    const sortedRanges = disabledDateRanges.map(range => ({
        start: dayjs(range.start),
        end: dayjs(range.end)
    })).sort((a, b) => a.start - b.start);

    // Calculate the valid date ranges
    const validRanges = [];
    let lastEnd = dayjs('0001-01-01');

    sortedRanges.forEach(range => {
        if (lastEnd.isBefore(range.start)) {
            validRanges.push({ start: lastEnd.add(1, 'day'), end: range.start.subtract(1, 'day') });
        }
        lastEnd = range.end;
    });

    // Add the final valid range if necessary
    if (lastEnd.isBefore(dayjs('9999-12-31'))) {
        validRanges.push({ start: lastEnd.add(1, 'day'), end: dayjs('9999-12-31') });
    }

    // Find the closest valid range to today
    let closestValidDate = null;
    let minDiff = Infinity;

    validRanges.forEach(range => {
        if (today.isBefore(range.start)) {
            const diff = range.start.diff(today);
            if (diff < minDiff) {
                minDiff = diff;
                closestValidDate = range.start;
            }
        } else if (today.isAfter(range.end)) {
            const diff = today.diff(range.end);
            if (diff < minDiff) {
                minDiff = diff;
                closestValidDate = range.end;
            }
        } else {
            closestValidDate = today;
            minDiff = 0;
        }
    });

    return closestValidDate.format('YYYY-MM-DDT00:00:00');
};

// PLAN

export const PlanSituation = ({ plan }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const iconStyle = {
        borderRadius: '50%',
        padding: '1px',
        backgroundColor: 'white',
        fontSize: '15px',
        color: '#011d89',
    };
    let icon = null;
    switch (plan.planStatus) {
        case 'IDEA':
            icon = getIconWithTooltip(Lightbulb, iconStyle, t('Idea'));
            break;
        case 'PENDING_APPROVAL':
            icon = getIconWithTooltip(Pending, iconStyle, t('Pending Approval'));
            break;
        case 'APPROVED':
            icon = getIconWithTooltip(ThumbUp, iconStyle, t('Approved'));
            break;
        default:
            icon = getIconWithTooltip(QuestionMark, iconStyle, t('Not Defined'));
    }
    return (
        <div className='doc-situation'>
            {icon}
        </div>
    )
}

export const PlanAccessLevel = ({ plan }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const iconStyle = {
        borderRadius: '50%',
        padding: '1px',
        backgroundColor: 'white',
        fontSize: '15px',
        color: '#011d89',
    };
    let icon = null;
    if (plan.readOnly === false) {
        icon = getIconWithTooltip(LockOpen, iconStyle, t('Can Edit'));
    } else {
        icon = getIconWithTooltip(LockPerson, iconStyle, t('Read Only'));
    }
    return (
        <div className='doc-access'>
            {icon}
        </div>
    )
}

export const AreasAndIndicatorsFromPlan = ({ plan }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const areasCount = plan.areaCount;
    const indicatorsCount = plan.indicatorCount;

    const areasText = areasCount === 1 ? t('Area') : t('Areas');
    const indicatorsText = indicatorsCount === 1 ? t('Indicator') : t('Indicators');

    return `${areasCount} ${areasText} | ${indicatorsCount} ${indicatorsText}`;
}


// export const getDocumentStatusOptions = () => {
//     const { t } = useB2BTranslation('b2b-plans');
//     return [
//         { value: 'IDEA', label: t('Idea') },
//         { value: 'PENDING_APPROVAL', label: t('Pending Approval') },
//         { value: 'APPROVED', label: t('Approved') },
//     ];
// }

// AREA

/**
 * Returns an array of disabled date ranges based on the provided area and other disabled date ranges.
 * 
 * @param {Object} area - The area object.
 * @param {Array} [otherDisabledDataRanges=null] - An optional array of other disabled date ranges.
 * @returns {Array} - An array of disabled date ranges.
 */

export const getAreaDisabledDataRanges = (area, otherDisabledDataRanges = null) => {
    const disabledDateRanges = otherDisabledDataRanges ? [...otherDisabledDataRanges] : [];
    if (!area || !area.areaStartDate || !area.areaEndDate) {
        return disabledDateRanges;
    }
    disabledDateRanges.push({
        start: '0001-01-01',
        end: dayjs(area.areaStartDate).subtract(1, 'day').format('YYYY-MM-DD')
    });
    disabledDateRanges.push({
        start: dayjs(area.areaEndDate).add(1, 'day').format('YYYY-MM-DD'),
        end: '9999-12-31'
    });
    // console.log('disabledDateRanges:', disabledDateRanges);
    return disabledDateRanges;
}

// OBJECTIVES

/**
 * Retrieves the current objective from the given array of objectiveDTOs.
 * An objective is considered current if its start date is on or before the current refence date
 * and its end date is on or after the current reference date.
 * If there are multiple current objectives, the one with the closest end date to the current date is returned.
 * If the objectiveDTOs array is empty or null, null is returned.
 *
 * @param {Array} objectiveDTOs - The array of objectiveDTOs to search for the current objective.
 * @returns {Object|null} - The current objective or null if no current objective is found.
 */

export const getCurrentObjective = (objectiveDTOs, referenceDate = null) => {
    // console.log('objectiveDTOs', objectiveDTOs);
    const currentReferenceDate = !referenceDate ? new Date() : referenceDate;
    const currentObjective = (objectiveDTOs && objectiveDTOs.length > 0) ?
        (objectiveDTOs.find(objective => {
            const startDate = new Date(objective.startDate);
            const endDate = new Date(objective.endDate);
            return startDate <= currentReferenceDate && endDate >= currentReferenceDate;
        }) || objectiveDTOs.reduce((closest, objective) => {
            const endDate = new Date(objective.endDate);
            const closestEndDate = new Date(closest.endDate);
            const closestDiff = Math.abs(closestEndDate - currentReferenceDate);
            const currentDiff = Math.abs(endDate - currentReferenceDate);
            return currentDiff < closestDiff ? objective : closest;
        }, objectiveDTOs[0])) : null;
    // console.log('currentObjective', currentObjective);
    return currentObjective;
}

/**
 * Retrieves the date ranges that should be disabled based on the given area indicator,
 * excluding the date range of a specific objective if provided.
 *
 * @param {Object} areaIndicator - The area indicator containing objectives and area data.
 * @param {string|null} [exceptObjectiveId=null] - The ID of the objective to exclude from the disabled date ranges.
 * @returns {Array<{start: string, end: string}>} An array of objects representing the disabled date ranges.
 */

export const getDisabledDateRanges = (areaIndicator, exceptObjectiveId = null) => {
    const disabledDateRanges = (areaIndicator.objectiveDTOs || [])
        .filter(objective => objective.objectiveId !== exceptObjectiveId)
        .map(objective => ({
            start: objective.startDate,
            end: objective.endDate,
        }));
    disabledDateRanges.push(...getAreaDisabledDataRanges(areaIndicator.area));
    // console.log('disabledDateRanges:', disabledDateRanges);
    return disabledDateRanges;
};



// AREA-INDICATORS & INDICATORS RELATED

export function isIndicatorDimensional(indicatorDTO) {
    return indicatorDTO && indicatorDTO.indicatorType === 'QUANTITATIVE_DIMENSIONAL';
}

export const getDimensionValueName = (dimensionOrder, dimensionalType) => {
    const dimensionValue = dimensionalType.dimensionValues.find(value => value.dimensionValueOrder === dimensionOrder);
    return dimensionValue ? `D${dimensionOrder} - ${dimensionValue.dimensionValueName}` : null;
};

export const getValuesFromDimensionalType = (dimensionalType) => {
    if (!dimensionalType) {
        return null;
    }
    return dimensionalType.dimensionValues.map((value) => {
        return {
            value: value.dimensionValueOrder,
            label: `D${value.dimensionValueOrder} - ${value.dimensionValueName}`,
        };
    });
}

/**
 * Creates a load factor payload from individual items.
 *
 * @param {...string} items - The items to include in the payload.
 * @returns {Object} The payload containing the items to load.
 */
export const createLoadFactor = (enumType, ...items) => {
    return {
        enumType: enumType,
        itemsToLoad: items
    };
};